/**
 * Horizontal swipe as a second way around (Željko's request).
 *
 * The orbit menu is the deliberate one — you choose where to go. This is the
 * lazy one: drag left to go one step further down the app's order, drag right to
 * come back. Direction and order are the same ones the slide already uses, so a
 * swipe and a tap on the menu produce the same movement.
 *
 * Nothing here follows the finger. A screen that tracks a drag has to be able to
 * come back if the drag is abandoned, which means both screens live in the DOM
 * for the whole gesture and every scroll position has to survive it. This is a
 * threshold instead: past 64px of mostly-sideways travel, the move the app
 * already knows how to make happens.
 */

/** How far, and how much more sideways than down, before it counts. */
const DISTANCE = 64
const RATIO = 1.4

/** Past this, it is a considered drag rather than a flick, and we let it be. */
const TIME_LIMIT = 700

/**
 * @param {(direction: 'forward'|'back') => void} onSwipe
 */
export function watchSwipes(onSwipe) {
  let start = null

  document.addEventListener(
    'touchstart',
    (event) => {
      start = null

      // Two fingers is a pinch or a scroll, never this.
      if (event.touches.length !== 1) return

      // Something is open over the screen and owns its own gestures.
      if (document.querySelector('.orbit, .lightbox') !== null) return

      // A gallery strip or a code block that can still scroll sideways under
      // the finger has first claim on the gesture.
      if (scrollableAcross(event.target)) return

      const touch = event.touches[0]

      start = { x: touch.clientX, y: touch.clientY, at: event.timeStamp }
    },
    { passive: true },
  )

  document.addEventListener(
    'touchmove',
    (event) => {
      if (start === null) return

      // A second finger mid-gesture means it was never a swipe.
      if (event.touches.length !== 1) start = null
    },
    { passive: true },
  )

  document.addEventListener(
    'touchend',
    (event) => {
      const from = start
      start = null

      if (from === null || event.changedTouches.length !== 1) return

      const touch = event.changedTouches[0]
      const dx = touch.clientX - from.x
      const dy = touch.clientY - from.y

      if (event.timeStamp - from.at > TIME_LIMIT) return
      if (Math.abs(dx) < DISTANCE) return
      if (Math.abs(dx) < Math.abs(dy) * RATIO) return

      // Recognised: swallow the click the browser would synthesise under the
      // finger, so a swipe that happens to end on a card does not open it.
      if (event.cancelable) event.preventDefault()

      onSwipe(dx < 0 ? 'forward' : 'back')
    },
    // Not passive: this one may need to cancel the click it would become.
    { passive: false },
  )
}

/** True when the touch began inside something that can still scroll sideways. */
function scrollableAcross(target) {
  let element = target instanceof Element ? target : null

  while (element !== null && element !== document.body) {
    const style = window.getComputedStyle(element)

    if (
      ['auto', 'scroll'].includes(style.overflowX) &&
      element.scrollWidth > element.clientWidth + 1
    ) {
      return true
    }

    element = element.parentElement
  }

  return false
}
